import { useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, selectUser } from '../../redux/slices/authSlice';
import { selectApplication, selectAppSaving } from '../../redux/slices/applicationSlice';
import { WIZARD_STEPS, isStepComplete, countCompletedSteps } from '../../constants/wizard';
import StatusPill from '../../components/statusPill';
import { AnimatePresence } from 'framer-motion';
import { resetDraft, clearExtraction } from '../../redux/slices/applicationSlice';
import { notify } from '../../utils/notify';
import './index.scss';

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.5l3 3 6-7"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const initialsOf = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

const ApplicantLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector(selectUser);
  const application = useSelector(selectApplication);
  const saving = useSelector(selectAppSaving);

  const completed = countCompletedSteps(application);
  const percent = Math.round((completed / WIZARD_STEPS.length) * 100);

  const activeIndex = WIZARD_STEPS.findIndex((s) => location.pathname.startsWith(s.path));

  // A step is reachable once every step before it is complete.
  const isReachable = (index) =>
    index === 0 || WIZARD_STEPS.slice(0, index).every((s) => isStepComplete(s.key, application));

  const handleStepClick = (step, index) => {
    if (!isReachable(index)) return;
    navigate(step.path);
  };

  // Track completion so we can pulse a marker only when it flips to complete.
  const prevComplete = useRef({});

  useEffect(() => {
    WIZARD_STEPS.forEach((step) => {
      prevComplete.current[step.key] = isStepComplete(step.key, application);
    });
  }, [application]);

  const handleStartOver = async () => {
    if (!application || application.status !== 'DRAFT') return;

    const confirmed = window.confirm(
      'This will clear everything you have entered and start a fresh application. Continue?'
    );
    if (!confirmed) return;

    const result = await dispatch(resetDraft(application.id));
    if (resetDraft.fulfilled.match(result)) {
      dispatch(clearExtraction());
      notify.info('Started a fresh application');
      navigate('/apply/personal');
    } else {
      notify.error(result.payload);
    }
  };

  return (
    <div className="applicant-shell">
      <header className="app-header">
        <div className="app-header__brand">
          <span className="app-header__mark">CreditFlow</span>
          <span className="app-header__role">Applicant</span>
        </div>

        <div className="app-header__right">
          {saving && (
            <span className="app-header__saving" role="status">
              <span className="app-header__saving-dot" />
              Saving
            </span>
          )}

          {application && <StatusPill status={application.status} size="sm" />}

          <div className="app-header__user">
            <span className="app-header__avatar" aria-hidden="true">
              {initialsOf(user?.username)}
            </span>
            <span className="app-header__user-meta">
              <span className="app-header__user-name">{user?.username}</span>
              <span className="app-header__user-sub">Loan applicant</span>
            </span>
          </div>

          {application?.status === 'DRAFT' && (
            <button type="button" className="applicant-header__startover" onClick={handleStartOver}>
              Start over
            </button>
          )}

          <button
            type="button"
            className="app-header__signout"
            onClick={() => dispatch(logoutUser())}>
            Sign out
          </button>
        </div>
      </header>

      <div className="app-body">
        <aside className="app-sidebar">
          <div className="progress-card">
            <span className="progress-card__label">Application progress</span>
            <div className="progress-card__track">
              <div className="progress-card__fill" style={{ width: `${percent}%` }} />
            </div>
            <span className="progress-card__value numeric">{percent}% complete</span>
          </div>

          <nav className="step-rail" aria-label="Application steps">
            <span className="step-rail__heading">Steps</span>

            <ol className="step-rail__list">
              {WIZARD_STEPS.map((step, index) => {
                const complete = isStepComplete(step.key, application);
                const justCompleted = complete && !prevComplete.current[step.key];
                const active = index === activeIndex;
                const reachable = isReachable(index);

                return (
                  <li
                    key={step.key}
                    className={[
                      'step-item',
                      complete && 'step-item--complete',
                      active && 'step-item--active',
                      !reachable && 'step-item--locked'
                    ]
                      .filter(Boolean)
                      .join(' ')}>
                    <button
                      type="button"
                      className="step-item__button"
                      onClick={() => handleStepClick(step, index)}
                      disabled={!reachable}
                      aria-current={active ? 'step' : undefined}>
                      <span
                        className={`step-item__marker ${justCompleted ? 'step-item__marker--pulse' : ''}`}>
                        {complete ? <CheckIcon /> : step.number}
                      </span>
                      <span className="step-item__text">
                        <span className="step-item__label">{step.label}</span>
                        <span className="step-item__hint">{step.hint}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          {application?.referenceNo && (
            <div className="ref-block">
              <span className="ref-block__label">Reference</span>
              <span className="ref-block__value numeric">{application.referenceNo}</span>
            </div>
          )}
        </aside>

        <main className="app-content">
          <AnimatePresence mode="wait">
            <div key={location.pathname} className="app-content__inner">
              <Outlet />
            </div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ApplicantLayout;
